import { DashboardMainContainer } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/DashboardMain/styled';
import {
	getClassificationGroup,
	getClassificationId,
	getDocumentListColumns,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/DashboardMain/utils';
import { useBulkDeleteDocuments } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/hooks/useBulkDeleteDocuments';
import { useBulkDocumentDownload } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/hooks/useBulkDocumentDownload';
import { useDocumentsWithFiltering } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/hooks/useDocumentsWithFiltering';
import {
	DocumentsLibraryDocumentRes,
	SelectedDocument,
	RightPanelAction,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/types';
import {
	toSelectedDocument,
	TreeItemsWithMeta,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/utils';
import { useMipsByDealId } from '@dealstack/frontend/app/pages/Project/MIP/hooks';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { DocumentTable } from '@dealstack/frontend/shared/components/Documents/DocumentTable/DocumentTable';
import { EmptyDocuments } from '@dealstack/frontend/shared/components/Documents/EmptyDocuments/EmptyDocuments';
import { usePersonsInDeal } from '@dealstack/frontend/shared/components/Documents/hooks/usePersonsInDeal';
import { usePreviewFile } from '@dealstack/frontend/shared/components/Documents/hooks/usePreviewFile';
import { DocumentViewerModalV2 } from '@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerModalV2';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { UseMantineTableDataOperationsPartialProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState';
import {
	TableOperations,
	useMantineTableWithPaginationFilteringSorting,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { DocumentsRes } from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import {
	useTranslationCommonUi,
	useTranslationDocuments,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useDownloadFileUsingFileStorageUrl } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadFileUsingFileStorageUrl';
import { NO_MIP_FILTER_VALUE } from '@dealstack/shared/lib/documents/types';
import { DocumentsFindByDealRes } from '@dealstack/shared/lib/generated';
import { Row, RowSelectionState } from '@tanstack/react-table';
import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import {
	BulkAction,
	BulkActionGroup,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton.types';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { Alert } from '@dealstack/frontend/shared/components/Alert/Alert';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { Box, Button, Flex } from '@mantine/core';
import {
	DOCUMENT_LIBRARY_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds';

const createBulkActions = ({
	tDocuments,
	bulkDownloadDocuments,
	shouldDisableActions: disableActions,
	bulkClassifyDocuments,
	addPersonsToDocuments,
	canDeleteDocuments,
	enterDeleteMode,
	openDeleteConfirm,
	totalRowCount,
}: {
	tDocuments: (key: string) => string;
	shouldDisableActions: boolean;
	bulkDownloadDocuments: (allDocumentsSelected: boolean, selectedDocumentIds: string[]) => void;
	bulkClassifyDocuments: (areAllRowsSelected: boolean) => void;
	addPersonsToDocuments: (areAllRowsSelected: boolean) => void;
	canDeleteDocuments: boolean;
	enterDeleteMode: () => void;
	openDeleteConfirm: (params: {
		allDocumentsSelected: boolean;
		selectedDocumentIds: string[];
		excludedDocumentIds: string[];
		attemptedCount: number;
	}) => void;
	totalRowCount: number;
}): BulkActionGroup<DocumentsFindByDealRes> => {
	const primary: BulkAction<DocumentsFindByDealRes>[] = [];
	const secondary: BulkAction<DocumentsFindByDealRes>[] = [];
	const special: BulkAction<DocumentsFindByDealRes>[] = [];

	primary.push(
		{
			key: 'download',
			label: tDocuments('bulk_actions_menu.download'),
			disabled: disableActions,
			onExecute: (table) => {
				const { greedySelecting } = getTableMeta(table);
				const selectedDocumentIds = Object.entries(table.getState().rowSelection)
					.filter(([, selected]) => selected)
					.map(([rowId]) => rowId);

				bulkDownloadDocuments(greedySelecting, selectedDocumentIds);
			},
		},
		{
			key: 'add_person',
			label: tDocuments('bulk_actions_menu.add_person'),
			disabled: disableActions,
			onExecute: (table) => {
				const { greedySelecting } = getTableMeta(table);
				addPersonsToDocuments(greedySelecting);
			},
		},
		{
			key: 'bulk-classify',
			label: tDocuments('bulk_actions_menu.bulk_classify'),
			disabled: disableActions,
			onExecute: (table) => {
				const { greedySelecting } = getTableMeta(table);
				bulkClassifyDocuments(greedySelecting);
			},
		}
	);

	if (canDeleteDocuments) {
		special.push({
			key: 'bulk-delete',
			label: tDocuments('bulk_actions_menu.delete'),
			color: 'red',
			disabled: disableActions,
			onSelect: () => enterDeleteMode(),
			onExecute: (table) => {
				const { greedySelecting } = getTableMeta(table);
				const rowSelectionEntries = Object.entries(table.getState().rowSelection);
				const selectedDocumentIds = rowSelectionEntries
					.filter(([, selected]) => selected)
					.map(([rowId]) => rowId);
				// In greedy mode, rows the user actively unchecked become exclusions on
				// "all matching filter." Otherwise excludedDocumentIds is unused.
				const excludedDocumentIds = rowSelectionEntries
					.filter(([, selected]) => !selected)
					.map(([rowId]) => rowId);
				const attemptedCount = greedySelecting
					? Math.max(totalRowCount - excludedDocumentIds.length, 0)
					: selectedDocumentIds.length;
				openDeleteConfirm({
					allDocumentsSelected: greedySelecting,
					selectedDocumentIds,
					excludedDocumentIds,
					attemptedCount,
				});
			},
		});
	}

	return { primary, secondary, special };
};

interface Props {
	dealId: string;
	rowSelection: RowSelectionState;
	setRowSelection: (state: RowSelectionState) => void;
	setAreAllRowsSelected: (areAllRowsSelected: boolean) => void;
	selectedTreeItem: TreeItemsWithMeta | undefined;
	setSelectedTreeItem: (treeItem: TreeItemsWithMeta | undefined) => void;
	rightPanelAction: RightPanelAction;
	setRightPanelAction: (action: RightPanelAction) => void;
	setSelectedDocument: (document: SelectedDocument | undefined) => void;
	handleArchiveDocument: (row: Row<DocumentsLibraryDocumentRes>) => void;
	setIsUploadActive: (isUploadActive: boolean) => void;
	documentsTableDataOperationsRef: RefObject<
		TableOperations<{
			relatedToIds: string[];
			updatedAt: { from: string; to: string };
			mipIds: string[];
		}>
	>;
}

export const DashboardMainPanel = ({
	dealId,
	rowSelection,
	setRowSelection,
	setAreAllRowsSelected,
	selectedTreeItem,
	rightPanelAction,
	setRightPanelAction,
	setSelectedDocument,
	handleArchiveDocument,
	setIsUploadActive,
	documentsTableDataOperationsRef,
}: Props) => {
	const tCommon = useTranslationCommonUi();
	const tDocuments = useTranslationDocuments();
	const { data: persons } = usePersonsInDeal(dealId);

	const getMipsRequest = useMipsByDealId(dealId);

	const mipOptions = useMemo(() => {
		// Filter out the synthetic "No MIP" entry the backend appends when fallback=true
		// (see NO_MIP_PROGRAME in libs/data-access/src/repositories/mip/validator.ts). We
		// surface our own NO_MIP option with a stable sentinel value instead.
		const mipOpts = (getMipsRequest.data ?? [])
			.filter((mip) => !!mip.id)
			.map((mip) => ({
				value: mip.id,
				label: mip.programmeName ?? 'Draft MIP',
			}));
		return [
			{ value: NO_MIP_FILTER_VALUE, label: tDocuments('documents.library.filter.no_mip') },
			...mipOpts,
		];
	}, [getMipsRequest.data, tDocuments]);

	const { downloadFile } = useDownloadFileUsingFileStorageUrl();
	const { previewDocument, previewFile, setPreviewDocument } = usePreviewFile();

	const handleRowClick = (row: Row<DocumentsLibraryDocumentRes>) => {
		setSelectedDocument(toSelectedDocument(row.original));
		if (
			rightPanelAction === RightPanelAction.NONE ||
			rightPanelAction === RightPanelAction.BULK_CLASSIFY ||
			rightPanelAction === RightPanelAction.BULK_ADD_PERSONS
		) {
			setRightPanelAction(RightPanelAction.EDIT_DOCUMENT);
		}
	};

	const handleBulkClassifyDocuments = (areAllRowsSelected: boolean) => {
		setAreAllRowsSelected(areAllRowsSelected);
		setRightPanelAction(RightPanelAction.BULK_CLASSIFY);
	};

	const handleAddingPersonsToDocuments = (areAllRowsSelected: boolean) => {
		setAreAllRowsSelected(areAllRowsSelected);
		setRightPanelAction(RightPanelAction.BULK_ADD_PERSONS);
	};

	// pagination options are shared between components so that they can refresh pagination depending on when they individually update state
	const columns = getDocumentListColumns({
		tDocuments,
		tCommon,
		persons,
		mipOptions,
		onRowClick: handleRowClick,
		downloadFile: (fileId: string, dealId: string, fileName: string) =>
			downloadFile({ fileId, dealId, fileName }),
		previewFile: (document: DocumentsRes, dealId: string) => previewFile(document, dealId),
		removeDocument: handleArchiveDocument,
		dealId,
	});

	// In delete mode the table only shows library-uploaded docs. Driven by a
	// server-side filter so meta.totalRowCount + pagination + "select all"
	// across pages all line up with the deletable set.
	const [isDeleteMode, setIsDeleteMode] = useState(false);

	const initialTableOptions: UseMantineTableDataOperationsPartialProps = {
		initialPagination: { pageIndex: 0, pageSize: 50 },
		queryScope: [selectedTreeItem?.id, isDeleteMode],
	};
	const useStateFromUrlParams = true;
	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting(
			columns,
			initialTableOptions,
			useStateFromUrlParams
		);

	const classificationId = getClassificationId(selectedTreeItem);
	const classificationGroup = getClassificationGroup(selectedTreeItem);

	const { data: documentsRes, isFetching: isDocumentsDataFetching } = useDocumentsWithFiltering({
		dealId,
		dataOperations,
		classificationId,
		classificationGroup,
		excludeWorkflowGenerated: isDeleteMode,
	});

	useEffect(() => {
		// @ts-ignore - this hack needs to be done because of issues with the table operations requiring a ref to work correctly
		documentsTableDataOperationsRef.current = dataOperations;
	}, [dataOperations, documentsTableDataOperationsRef]);

	// This can be moved to Dashboard after in page breadcrumbs feature flag is removed
	useBreadcrumbs([
		{
			name: tDocuments('documents.library.breadcrumb'),
			url: `#`,
		},
	]);

	const { mutate: bulkDownloadDocuments, isPending: isBulkDownloadLoading } =
		useBulkDocumentDownload({
			dealId,
			tDocuments,
		});

	// Resets the table's bulk-action mode (clears selection, greedy "all pages" state,
	// and the blue selection toolbar). Set by DocumentTable via actionResetRef.
	const actionResetRef = useRef<(() => void) | null>(null);

	const handleBulkDownloadDocuments = (
		allDocumentsSelected: boolean,
		selectedDocumentIds: string[]
	) => {
		bulkDownloadDocuments({
			dealId,
			allDocumentsSelected,
			documentIds: selectedDocumentIds,
			filters: {
				searchQuery: documentsTableDataOperationsRef.current?.search,
				relatedToIds: documentsTableDataOperationsRef.current?.filter?.relatedToIds,
				mipIds: documentsTableDataOperationsRef.current?.filter?.mipIds,
				classificationId,
				classificationGroup,
				dateRange: {
					from: documentsTableDataOperationsRef.current?.filter?.updatedAt?.from,
					to: documentsTableDataOperationsRef.current?.filter?.updatedAt?.to,
				},
			},
		});

		// The download is fired (it enqueues an async job); the user is done selecting,
		// so drop out of selection mode and clear the selection.
		actionResetRef.current?.();
		setRowSelection({});
	};

	// --- Bulk delete (delete-mode) state & wiring ---

	const { hasPermission: canDeleteDocuments } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: dealId,
		permissionRole: PermissionRole.EDITOR,
	});

	const [confirmDrawerOpen, setConfirmDrawerOpen] = useState(false);
	const [pendingDeleteSelection, setPendingDeleteSelection] = useState<{
		allDocumentsSelected: boolean;
		selectedDocumentIds: string[];
		excludedDocumentIds: string[];
		attemptedCount: number;
	}>({
		allDocumentsSelected: false,
		selectedDocumentIds: [],
		excludedDocumentIds: [],
		attemptedCount: 0,
	});
	const [deleteSuccess, setDeleteSuccess] = useState(false);

	const { bulkDelete, isPending: isBulkDeletePending } = useBulkDeleteDocuments({
		dealId,
		onSuccess: () => {
			setDeleteSuccess(true);
			// Exit delete mode after a successful delete; the drawer auto-closes via deleteSuccess.
			actionResetRef.current?.();
			setIsDeleteMode(false);
			setPendingDeleteSelection({
				allDocumentsSelected: false,
				selectedDocumentIds: [],
				excludedDocumentIds: [],
				attemptedCount: 0,
			});
		},
	});

	const enterDeleteMode = () => {
		setRowSelection({});
		setIsDeleteMode(true);
	};

	const exitDeleteMode = () => {
		actionResetRef.current?.();
		setIsDeleteMode(false);
		setRowSelection({});
	};

	const openDeleteConfirm = (params: {
		allDocumentsSelected: boolean;
		selectedDocumentIds: string[];
		excludedDocumentIds: string[];
		attemptedCount: number;
	}) => {
		setPendingDeleteSelection(params);
		setDeleteSuccess(false);
		setConfirmDrawerOpen(true);
	};

	const handleConfirmedDelete = async () => {
		const filters = {
			searchQuery: documentsTableDataOperationsRef.current?.search,
			relatedToIds: documentsTableDataOperationsRef.current?.filter?.relatedToIds,
			mipIds: documentsTableDataOperationsRef.current?.filter?.mipIds,
			classificationId,
			classificationGroup,
			dateRange: {
				from: documentsTableDataOperationsRef.current?.filter?.updatedAt?.from,
				to: documentsTableDataOperationsRef.current?.filter?.updatedAt?.to,
			},
		};
		try {
			await bulkDelete({
				allDocumentsSelected: pendingDeleteSelection.allDocumentsSelected,
				selectedDocumentIds: pendingDeleteSelection.selectedDocumentIds,
				excludedDocumentIds: pendingDeleteSelection.allDocumentsSelected
					? pendingDeleteSelection.excludedDocumentIds
					: undefined,
				filters,
			});
		} catch {
			// onError in the hook already fires a toast; swallow here to avoid unhandled-rejection.
		}
	};

	const hasDocuments = !!documentsRes?.documents.length;

	const hasFilterApplied = mantineTableDataOperations.isFiltersApplied || !!selectedTreeItem;

	if (!hasDocuments && !hasFilterApplied && !isDocumentsDataFetching) {
		return (
			<DashboardMainContainer>
				<EmptyDocuments
					setIsUploadActive={setIsUploadActive}
					headerText={tDocuments('documents.library.empty_body')}
				/>
			</DashboardMainContainer>
		);
	}

	return (
		<DashboardMainContainer>
			{isDeleteMode && (
				<Box mb="md">
					<Alert state="warning" size="m">
						<Flex justify="space-between" align="center" gap="md">
							<span>{tDocuments('bulk_delete.banner.text')}</span>
							<Button variant="subtle" size="sm" onClick={exitDeleteMode}>
								{tDocuments('bulk_delete.banner.cancel')}
							</Button>
						</Flex>
					</Alert>
				</Box>
			)}
			<DocumentTable<DocumentsFindByDealRes>
				getRowId={(row) => row.id}
				dataTestId={DOCUMENT_LIBRARY_ELEMENT_ID.DOCUMENTS_TABLE}
				// Suffixed on the document id, the same key getRowId uses. Names are not unique
				// in the library - the upload wizard only rejects repeats within one batch - so a
				// name-keyed testid can match several rows at once.
				rowDataTestId={(row) =>
					makeComplexDataTestId(DOCUMENT_LIBRARY_ELEMENT_ID.DOCUMENT_ROW, row.original.id)
				}
				isLoading={isDocumentsDataFetching}
				hasNoTopToolbar={true}
				renderBulkActionsMenu={true}
				enableColumnResizing={true}
				actionResetRef={actionResetRef}
				onActionCanceled={() => {
					if (isDeleteMode) {
						setIsDeleteMode(false);
						setRowSelection({});
					}
				}}
				actions={() =>
					createBulkActions({
						tDocuments,
						bulkDownloadDocuments: handleBulkDownloadDocuments,
						shouldDisableActions: isBulkDownloadLoading,
						bulkClassifyDocuments: handleBulkClassifyDocuments,
						addPersonsToDocuments: handleAddingPersonsToDocuments,
						canDeleteDocuments,
						enterDeleteMode,
						openDeleteConfirm,
						totalRowCount: documentsRes?.meta.totalRowCount ?? 0,
					})
				}
				state={{ rowSelection }}
				onRowSelectionChange={(state: RowSelectionState) => {
					setRowSelection(state);
					if (rightPanelAction === RightPanelAction.BULK_CLASSIFY) {
						setRightPanelAction(RightPanelAction.NONE);
					}
				}}
				dataOperations={mantineTableDataOperations}
				data={documentsRes?.documents}
				rowCount={documentsRes?.meta.totalRowCount}
				columns={columns}
				enableRowSelection={true}
				onRowClick={handleRowClick}
			/>
			<ConfirmDeleteDrawer
				opened={confirmDrawerOpen}
				onClose={() => setConfirmDrawerOpen(false)}
				title={tDocuments('bulk_delete.modal.title', {
					count: pendingDeleteSelection.attemptedCount,
				})}
				alertDescription={tDocuments('bulk_delete.modal.body', {
					count: pendingDeleteSelection.attemptedCount,
				})}
				confirmDeleteText="delete"
				wordMismatchErrorMessage={tDocuments('bulk_delete.modal.word_mismatch')}
				inputLabel={tDocuments('bulk_delete.modal.input_label')}
				inputPlaceholder="delete"
				deleteBtnText={tDocuments('bulk_delete.modal.delete_button')}
				cancelBtnText={tDocuments('bulk_delete.modal.cancel_button')}
				enableDeleteButtonOnlyOnConfirmDelete={true}
				handleDelete={handleConfirmedDelete}
				deleteLoading={isBulkDeletePending}
				deleteSuccess={deleteSuccess}
			/>
			{previewDocument && (
				<DocumentViewerModalV2
					opened={!!previewDocument}
					onClose={() => setPreviewDocument(undefined)}
					file={previewDocument.downloadUrl}
					fileExtension={previewDocument.fileExtension}
					fileName={previewDocument.fileName}
					isLoading={false}
					showDetailsByDefault={false}
				/>
			)}
		</DashboardMainContainer>
	);
};
